import {Component, Inject, Vue} from "vue-property-decorator";
import Utils from "@/vue/infrastructure/utils/helpers";
import VueBarcode from "vue-barcode";
import { IMaskDirective } from "vue-imask";
import DfStore from "@/vue/domain/store/df-store";
import DfUser from "@/vue/domain/user/df-user";
import DfBrand from "@/vue/domain/brand/df-brand";
import Swal from "sweetalert2";
import {CONTAINER_TYPES} from "@/vue/infrastructure/ioc/container-types";
import ServiceInterface from "@/vue/service/service-interface";
import DfContent from "@/vue/domain/content/df-content";
import moment from "moment";

const DfHeaderMobileComponent = () =>
  Utils.externalComponent2("df-header-mobile");
const DfStoreCardComponent = () => Utils.externalComponent2("df-store-card");

@Component({
  components: {
    DfLoadingMask: () => import(/* webpackChunkName: 'df-loading-mask' */ "@/vue/components/df-loading-mask/df-loading-mask.vue"),
    DfHeaderMobile: DfHeaderMobileComponent,
    DfStoreCard: DfStoreCardComponent,
    VueBarcode,
  },
  directives: {
    imask: IMaskDirective,
  },
})
export default class DfPageAccountCardComponent extends Vue {
  @Inject(CONTAINER_TYPES.ServiceInterface)
  private service!: ServiceInterface;

  private clubCardNumberError = false;
  private unbindWorking = false;
  private userLoading = false;
  private cardPoints: any = {
    total: 0,
    date: "",
    hour: "",
  };

  get brand(): DfBrand {
    return this.$store.getters.brand;
  }

  get clubCardNumber(): DfUser {
    return Utils.getPropertyValue(
      this.currentUser,
      Utils.PROPERTY_USER_CAMI_CARD_NUMBER,
      "STRING"
    );
  }

  get currentStore(): DfStore {
    return this.$store.getters.currentStore;
  }

  get currentUser(): DfUser {
    return this.$store.getters.user;
  }

  get fullName(): string {
    const firstName: string = Utils.getPropertyValue(
      this.currentUser,
      "FIRST_NAME",
      "STRING",
      ""
    );
    const lastName: string = Utils.getPropertyValue(
      this.currentUser,
      "LAST_NAME",
      "STRING",
      ""
    );
    const fullName = [firstName, lastName].join(" ");
    return fullName.trim().length > 0 ? fullName : null;
  }

  get clubCardBackgroundImage(): any {
    const bgCardAdvanced: string = Utils.getPropertyImageUrl(this.brand, Utils.PROPERTY_CARD_BACKGROUND_IMAGE, "");
    const bgCardStandard: string = Utils.getPropertyImageUrl(this.brand, Utils.PROPERTY_CARD_CLUB_MY_AREA_BACKGROUND_IMAGE, "");

    return bgCardAdvanced || bgCardStandard;
  }

  get extraText(): boolean {
    return this.brand.alias === "dpiu-maxidi";
  }

  get registrationMode(): string {
    const propertyValue: any = Utils.getPropertyValue(this.brand, Utils.PROPERTY_REGISTRATION_MODE, "LIST");
    return propertyValue ? propertyValue.key : Utils.REGISTRATION_MODE_STANDARD;
  }

  get showAdvancedActions(): boolean {
    return this.registrationMode !== Utils.REGISTRATION_MODE_STANDARD && !!this.currentUser;
  }

  get showCardPoints(): boolean {
    return process.env.VUE_APP_REDIRECT_BASE_URL === "true";
  }

  beforeMount() {
    this.userLoading = true;
    this.$root.$emit("loadUser", () => { this.userLoading = false;});
    if (Utils.enablePrivateArea() && this.showCardPoints) {
      this.loadUserCardPoints();
    }
  }

  private bindCamiCard() {
    if (this.clubCardNumber) {
      this.$router.push({name: "user-registration", params: {storeAlias: this.currentStore.alias}, query: { action: "newcard" }});
    } else {
      this.$router.push({name: "user-registration", params: {storeAlias: this.currentStore.alias}, query: { action: "card" }});
    }
  }

  private transferCamiPoints() {
    this.$router.push({ name: "user-registration", params: { storeAlias: this.currentStore.alias }, query: { action: "transfer" }});
  }

  private goToStorePage(store: DfStore) {
    this.$router
      .push({ name: "store", params: { storeAlias: store.alias } })
      .catch((err) => err);
  }

  private openMap() {
    window.open(
      `${Utils.GOOGLE_MAP_URL}${Utils.storeGoogleMapsCoordinates(
        this.currentStore
      )}`,
      "_blank"
    );
  }

  private unbindCamiCard() {
    Swal.fire({
      title: this.$t("df-page-user-registration.modal.request-unbind-cami-card.save.title").toString(),
      text: this.$t("df-page-user-registration.modal.request-unbind-cami-card.save.body").toString(),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: this.$t("df-page-user-registration.modal.request-unbind-cami-card.save.ok").toString(),
      cancelButtonText: this.$t("df-page-user-registration.modal.request-unbind-cami-card.save.cancel").toString(),
      input: "password",
      inputPlaceholder: this.$t("df-page-user-registration.modal.request-unbind-cami-card.password").toString(),
    }).then((result) => {
      if (result.value) {
        this.unbindWorking = true;
        this.service
          .unbindCamiCard({ password: result.value})
          .then((response: any) => {
            this.unbindWorking = false;
            this.$root.$emit("loadUser");

            Swal.fire({
              title: this.$t("df-page-user-registration.modal.unbind-cami-card.save.title").toString(),
              text: this.$t("df-page-user-registration.modal.unbind-cami-card.save.body").toString(),
              icon: "info",
              confirmButtonColor: "#3085d6",
              confirmButtonText: this.$t("df-page-user-registration.modal.unbind-cami-card.save.ok").toString(),
            });
          })
          .catch((error: Error) => {
            this.unbindWorking = false;
            Utils.defaultApiErrorHandler(error, this, () => { this.unbindCamiCard() });
          });
      }
    });
  }

  private loadUserCardPoints() {
    if (this.currentUser) {
      this.service
        .getUserContents({ typeCode: ["PNT"] })
        .then((data: any) => {
          const cardPoints: Array<DfContent> = data.userContents
            .filter((content: DfContent) => content.type.code === "PNT")
            .map((content: DfContent) => {
              const date: string = Utils.getPropertyValue(content, Utils.PROPERTY_PRIVATE_CASHBACK_LAST_UPDATE, "DATE");
              const total: string = Utils.getPropertyValue(content, Utils.PROPERTY_PRIVATE_POINTS_BALANCE, "NUMBER", 0);
              return {
                total: total,
                date: date ? moment(date, Utils.ISO_8601_STRING_PATTERN).format(Utils.DATE_STRING_PATTER) : "",
                hour: date ? moment(date, Utils.ISO_8601_STRING_PATTERN).format(Utils.DATE_STRING_HOURS_MINUTES_SECONDS) : "",
              };
            });
          this.cardPoints = cardPoints.length ? cardPoints[0] : this.cardPoints;
        })
        .catch((error: Error) => {
          Utils.defaultApiErrorHandler(error, this, () => { this.loadUserCardPoints() });
        });
    }
  }
}
